import axios from 'axios';

/**
 * Cliente único para hablar con la API.
 *
 * Antes cada pantalla armaba sus llamadas por separado y repetía la lógica de
 * las cabeceras. Aquí queda en un solo lugar: el token correcto se adjunta
 * según la ruta, y los errores llegan siempre con un mensaje mostrable.
 */

const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Claves de almacenamiento. El token del administrador va aparte a propósito:
// que una sesión de paciente no pueda tocar el panel ni por accidente.
export const STORAGE = {
  token: 'anonimamente_token',
  user: 'anonimamente_user',
  code: 'anonimamente_code',
  adminToken: 'admin_token',
  adminUser: 'admin_user',
};

export const api = axios.create({
  baseURL: `${BASE_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const esAdmin = (config.url || '').startsWith('/admin');
  const token = sessionStorage.getItem(esAdmin ? STORAGE.adminToken : STORAGE.token);

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

/** Saca el mensaje que corresponde mostrarle a la persona. */
export function mensajeDeError(error, porDefecto = 'Algo salió mal. Intenta nuevamente.') {
  const detalle = error?.response?.data?.detail;

  if (typeof detalle === 'string' && detalle.trim() !== '') {
    return detalle;
  }

  if (error?.code === 'ERR_NETWORK') {
    return 'No pudimos conectar con el servidor. Revisa tu conexión.';
  }

  return porDefecto;
}

/** Guarda la sesión del paciente. */
export function guardarSesion({ access_token, pseudonym, patient_id }) {
  sessionStorage.setItem(STORAGE.token, access_token);
  sessionStorage.setItem(
    STORAGE.user,
    JSON.stringify({ pseudonym, patient_id, type: 'patient' })
  );
}

export function leerSesion() {
  const token = sessionStorage.getItem(STORAGE.token);
  const bruto = sessionStorage.getItem(STORAGE.user);

  if (!token || !bruto) return null;

  try {
    return { token, ...JSON.parse(bruto) };
  } catch {
    return null;
  }
}

export function borrarSesion() {
  sessionStorage.removeItem(STORAGE.token);
  sessionStorage.removeItem(STORAGE.user);
}

/**
 * El código de continuidad se guarda en localStorage y no en sessionStorage,
 * porque su gracia es sobrevivir al cierre del navegador. Es la única cosa que
 * dejamos persistida en el equipo del paciente, y se puede borrar desde el
 * portal en cualquier momento.
 */
export function recordarCodigo(code) {
  try {
    localStorage.setItem(STORAGE.code, code);
  } catch {
    // Si el navegador bloquea el almacenamiento, el paciente igual vio el
    // código en pantalla y se le pidió guardarlo.
  }
}

export function codigoRecordado() {
  try {
    return localStorage.getItem(STORAGE.code) || '';
  } catch {
    return '';
  }
}

export function olvidarCodigo() {
  try {
    localStorage.removeItem(STORAGE.code);
  } catch {
    /* nada que hacer */
  }
}

/** Formatea un monto en pesos chilenos. */
export function pesos(monto) {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(monto) || 0);
}

export default api;
